import { BackButton } from "@/components/ui/BackButton";
import { OPTIONAL_VEHICLE_FIELDS } from "@/constants/registation-form";
import {
  Button,
  Card,
  Col,
  Form,
  Grid,
  Input,
  message,
  Row,
  Select,
  Spin,
  Typography,
} from "antd";
import { useState } from "react";
import type {
  RegistrationRequest,
  GetFormReferenceResponse,
  CategoryKey,
  RegistrationResponse,
} from "@/features/public/types/public";
import { useLoading } from "@/hooks/use-loading";
import { getReferences, registration } from "@/services/public.service";
import { useEffect } from "react";
import {
  buildRegistrationPayload,
  deriveAgeFromMyKad,
  isAgeConsistentWithBirthYear,
  normalizeIdentity,
} from "./utils/registration-form.utils";
import {
  renderLabel,
  renderRequiredInputItem,
  renderRequiredSelectItem,
  requiredRule,
} from "./utils/registration-form.fields";
import { Loader2 } from "lucide-react";
import toSelectOptions from "./helpers/select.helper";

type RegistrationFormProps = {
  category: CategoryKey;
  identityNo: string;
  prefill?: Partial<RegistrationRequest>;
  onBack: () => void;
  onSubmit: (data: RegistrationResponse) => void;
};

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const REQUIRED_COLOR = "#C00";

const REQUIRED_FIELDS: (keyof RegistrationRequest)[] = [
  "firstName",
  "lastName",
  "identityNo",
  "mdGenderKey",
  "age",
  "yearOfBirth",
  "phoneNumber",
  "mdVillageId",
  "mdResidentDurationKey",
  "address1",
  "mdOccupationKey",
  "mdHouseStatusKey",
  "houseBenchmark",
  "mdFerryFrequencyKey",
];

const CATEGORY_DISPLAY_NAME: Record<CategoryKey, string> = {
  resident: "Mastautin",
  "non-resident": "Bukan Mastautin",
};

export function RegistrationForm({
  category,
  identityNo,
  prefill,
  onBack,
  onSubmit,
}: RegistrationFormProps) {
  const screens = useBreakpoint();
  const [references, setReferences] = useState<GetFormReferenceResponse | null>(
    null,
  );
  const { loading, withLoading } = useLoading();
  const [form] = Form.useForm<RegistrationRequest>();

  const normalizedIdentity = normalizeIdentity(identityNo);
  const derivedAge = deriveAgeFromMyKad(normalizedIdentity);
  const shouldLockAge = derivedAge !== undefined;

  const values = Form.useWatch([], form) ?? {};
  const selectedOccupationKey = Form.useWatch("mdOccupationKey", form);
  const selectedHouseStatusKey = Form.useWatch("mdHouseStatusKey", form);

  const hasValue = (value: unknown) => {
    if (typeof value === "string") return value.trim().length > 0;
    if (typeof value === "number") return Number.isFinite(value);
    return value != null;
  };

  const isOtherOption = (value: unknown) => {
    if (value === null || value === undefined || value === "") {
      return false;
    }

    const normalized = String(value)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

    return (
      normalized === "other" ||
      normalized === "others" ||
      normalized === "lainlain" ||
      normalized === "lainlainlain"
    );
  };

  const showOccupationDetail = isOtherOption(selectedOccupationKey);
  const showHouseStatusDetail = isOtherOption(selectedHouseStatusKey);

  useEffect(() => {
    if (!showOccupationDetail) {
      form.setFieldValue("occupation", undefined);
    }

    if (!showHouseStatusDetail) {
      form.setFieldValue("houseDetails", undefined);
    }
  }, [showOccupationDetail, showHouseStatusDetail, form]);

  const canSubmit =
    REQUIRED_FIELDS.every((field) => hasValue(values[field])) &&
    (!showOccupationDetail || hasValue(values.occupation)) &&
    (!showHouseStatusDetail || hasValue(values.houseDetails));

  const submit = async (submittedValues: RegistrationRequest) => {
    const payload = buildRegistrationPayload({
      categoryKey: category,
      submittedValues,
    });

    try {
      const res = await withLoading(() => registration(payload));

      if (res.status && res.data) {
        onSubmit(res.data);
      } else {
        message.error("Pendaftaran gagal. Sila cuba lagi.");
      }
    } catch (error) {
      console.error(error);
      message.error("Pendaftaran gagal. Sila cuba lagi.");
    }
  };

  useEffect(() => {
    const loadReferences = async () => {
      try {
        const res = await withLoading(() =>
          getReferences({
            categoryKey: category,
            languageCode: "ms",
          }),
        );

        if (res.status) {
          setReferences(res.data);
        }
      } catch (error) {
        console.error(error);
        message.error("Gagal mendapatkan maklumat borang.");
      }
    };

    loadReferences();
  }, [category]);

  useEffect(() => {
    form.setFieldsValue({
      identityNo: normalizedIdentity || undefined,
      age: derivedAge,
    });
  }, [normalizedIdentity, derivedAge, form]);

  useEffect(() => {
    if (!prefill) return;

    form.setFieldsValue({
      ...prefill,
      identityNo: normalizedIdentity || prefill.identityNo,
      yearOfBirth:
        typeof prefill.yearOfBirth === "number"
          ? prefill.yearOfBirth
          : undefined,
      registrationRemark: prefill.registrationRemark,
    });
  }, [prefill, normalizedIdentity, form]);

  return (
    <>
      {loading && (
        <Spin
          fullscreen
          description="Memuatkan..."
          indicator={
            <Loader2 className="animate-spin" style={{ fontSize: 48 }} />
          }
        />
      )}

      <div className="mt-4">
        <div className="relative sm:flex sm:items-center sm:justify-center">
          <div className="sm:absolute sm:left-0 sm:w-full">
            <BackButton onClick={onBack} />
          </div>

          <div className="text-center flex flex-col gap-2">
            <Title
              level={3}
              style={{
                margin: 0,
                color: "#272728",
                fontWeight: 700,
                fontSize: screens.sm ? 24 : 18,
                marginTop: screens.sm ? 0 : 16,
                textTransform: "uppercase",
              }}
            >
              Maklumat Peribadi: {CATEGORY_DISPLAY_NAME[category]}
            </Title>

            <Text
              style={{
                color: "#272728",
                fontWeight: 500,
              }}
            >
              Medan bertanda <Text style={{ color: REQUIRED_COLOR }}>*</Text>{" "}
              wajib diisi
            </Text>
          </div>
        </div>

        <div className="mt-4">
          <Card>
            <Form
              form={form}
              layout="vertical"
              requiredMark={false}
              onFinish={submit}
              scrollToFirstError
            >
              <Row gutter={[40, 0]}>
                <Col xs={24} md={12} lg={6}>
                  {renderRequiredInputItem(
                    "firstName",
                    "Nama Pertama",
                    "Sila masukkan nama pertama",
                    "Contoh: Ahmad",
                    50,
                  )}
                </Col>

                <Col xs={24} md={12} lg={6}>
                  {renderRequiredInputItem(
                    "lastName",
                    "Nama Akhir",
                    "Sila masukkan nama akhir",
                    "Contoh: Bin Ahmad",
                    50,
                  )}
                </Col>

                <Col xs={24} md={12} lg={4}>
                  <Form.Item
                    label={renderLabel("No. MyKad/ Pasport")}
                    name="identityNo"
                  >
                    <Input disabled size="large" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12} lg={4}>
                  <Form.Item
                    label={renderLabel("Umur", true)}
                    name="age"
                    dependencies={["yearOfBirth"]}
                    rules={[
                      ...requiredRule("Sila masukkan umur"),
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const age = Number(value);
                          const yearOfBirth = Number(
                            getFieldValue("yearOfBirth"),
                          );

                          if (
                            !Number.isFinite(age) ||
                            !Number.isFinite(yearOfBirth)
                          ) {
                            return Promise.resolve();
                          }

                          if (isAgeConsistentWithBirthYear(age, yearOfBirth)) {
                            return Promise.resolve();
                          }

                          return Promise.reject(
                            new Error("Umur tidak sepadan dengan tahun lahir"),
                          );
                        },
                      }),
                    ]}
                    getValueFromEvent={(e) => {
                      const value = String(e.target.value || "").replace(
                        /[^\d]/g,
                        "",
                      );
                      return value ? Number(value) : undefined;
                    }}
                  >
                    <Input
                      size="large"
                      placeholder="Contoh: 30"
                      disabled={shouldLockAge}
                      inputMode="numeric"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12} lg={4}>
                  {renderRequiredSelectItem(
                    "mdGenderKey",
                    "Jantina",
                    "Sila masukkan jantina",
                    "Pilih jantina",
                    references?.genderLists.map((item) => ({
                      value: item.key,
                      label: item.key === "male" ? "LELAKI" : "PEREMPUAN",
                    })) ?? [],
                  )}
                </Col>
              </Row>

              <Row gutter={[40, 0]}>
                <Col xs={24} md={12} lg={6}>
                  {renderRequiredSelectItem(
                    "yearOfBirth",
                    "Tahun Lahir",
                    "Sila masukkan tahun lahir",
                    "Pilih tahun lahir",
                    references?.birthYearLists.map((year) => ({
                      value: year,
                      label: year.toString(),
                    })) ?? [],
                    undefined,
                    [
                      ({ getFieldValue }) => ({
                        validator(_: unknown, value: unknown) {
                          const yearOfBirth = Number(value);
                          const age = Number(getFieldValue("age"));

                          if (
                            !Number.isFinite(age) ||
                            !Number.isFinite(yearOfBirth)
                          ) {
                            return Promise.resolve();
                          }

                          if (isAgeConsistentWithBirthYear(age, yearOfBirth)) {
                            return Promise.resolve();
                          }

                          return Promise.reject(
                            new Error("Tahun lahir tidak sepadan dengan umur"),
                          );
                        },
                      }),
                    ],
                    ["age"],
                  )}
                </Col>

                <Col xs={24} md={12} lg={6}>
                  <Form.Item
                    label={renderLabel("Nombor telefon", true)}
                    name="phoneNumber"
                    rules={requiredRule("Sila masukkan nombor telefon")}
                  >
                    <Input
                      size="large"
                      placeholder="Contoh: 0123456789"
                      maxLength={15}
                      inputMode="numeric"
                      onChange={(e) => {
                        const nextValue = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 15);
                        form.setFieldValue("phoneNumber", nextValue);
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12} lg={6}>
                  {renderRequiredSelectItem(
                    "mdVillageId",
                    "Kampung",
                    "Sila masukkan kampung",
                    "Pilih kampung",
                    references?.villageLists.map((village) => ({
                      value: village.mdVillageId,
                      label: village.name,
                    })) ?? [],
                  )}
                </Col>

                <Col xs={24} md={12} lg={6}>
                  {renderRequiredSelectItem(
                    "mdResidentDurationKey",
                    "Tempoh menetap",
                    "Sila masukkan tempoh menetap",
                    "Pilih tempoh menetap",
                    toSelectOptions(references?.residentDurationLists ?? []),
                  )}
                </Col>
              </Row>

              <div className="border-t border-[#EEE] sm:mt-4 py-4 sm:py-10">
                <Row gutter={[40, 0]}>
                  <Col xs={24} md={12} lg={12}>
                    <Form.Item label={renderLabel("Alamat", true)}>
                      <Form.Item
                        name="address1"
                        rules={requiredRule("Sila masukkan alamat")}
                        style={{ marginBottom: 8 }}
                      >
                        <Input
                          placeholder="Contoh: LOT 100, Kampung Tekek"
                          size="large"
                          maxLength={85}
                        />
                      </Form.Item>

                      <Form.Item name="address2" style={{ marginBottom: 0 }}>
                        <Input
                          placeholder="Contoh: Pulau Tioman, 26800 Rompin, Pahang"
                          size="large"
                          maxLength={170}
                        />
                      </Form.Item>
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12} lg={6}>
                    {renderRequiredSelectItem(
                      "mdOccupationKey",
                      "Pekerjaan",
                      "Sila masukkan pekerjaan",
                      "Pilih pekerjaan",
                      toSelectOptions(references?.occupationLists ?? []),
                    )}

                     {showOccupationDetail && (
                      <Form.Item
                        label={renderLabel("Nyatakan pekerjaan", true)}
                        name="occupation"
                        rules={requiredRule("Sila nyatakan pekerjaan")}
                      >
                        <Input
                          placeholder="Contoh: Pelajar"
                          size="large"
                          maxLength={50}
                        />
                      </Form.Item>
                    )}

                    {renderRequiredInputItem(
                      "houseBenchmark",
                      "Landmark rumah",
                      "Sila masukkan landmark rumah",
                      "Contoh: Depan Masjid Tekek",
                      50,
                    )}
                  </Col>

                  <Col xs={24} sm={12} lg={6}>
                    {renderRequiredSelectItem(
                      "mdHouseStatusKey",
                      "Status rumah kediaman",
                      "Sila masukkan status rumah kediaman",
                      "Pilih status kediaman",
                      toSelectOptions(references?.houseStatusLists ?? []),
                    )}

                    {showHouseStatusDetail && (
                      <Form.Item
                        label={renderLabel(
                          "Nyatakan kediaman",
                          true,
                        )}
                        name="houseDetails"
                        rules={requiredRule(
                          "Nyatakan kediaman",
                        )}
                      >
                        <Input
                          placeholder="Contoh: Asrama"
                          size="large"
                          maxLength={50}
                        />
                      </Form.Item>
                    )}

                    {renderRequiredSelectItem(
                      "mdFerryFrequencyKey",
                      "Kekerapan Penggunaan Feri",
                      "Sila masukkan Kekerapan Penggunaan Feri",
                      "Pilih Kekerapan",
                      toSelectOptions(references?.ferryFrequencyLists ?? []),
                    )}
                  </Col>
                </Row>
              </div>

              <div className="border-t border-[#EEE] pt-4 sm:pt-10">
                <Row gutter={[40, 0]}>
                  {OPTIONAL_VEHICLE_FIELDS.map((field) => {
                    if (field.options) {
                      return (
                        <Col key={field.name} xs={24} sm={12} lg={6}>
                          <Form.Item
                            label={renderLabel(field.label)}
                            name={field.name}
                          >
                            <Select
                              size="large"
                              placeholder={field.placeholder}
                              options={field.options}
                              allowClear
                              showSearch
                            />
                          </Form.Item>
                        </Col>
                      );
                    }

                    return (
                      <Col key={field.name} xs={24} sm={12} lg={6}>
                        <Form.Item
                          label={renderLabel(field.label)}
                          name={field.name}
                        >
                          <Input
                            type={field.type}
                            placeholder={field.placeholder}
                            size="large"
                            maxLength={field.maxLength}
                            onChange={(e) => {
                              const maxLength = field.maxLength ?? 255;
                              const value = e.target.value.slice(0, maxLength);

                              if (value !== e.target.value) {
                                form.setFieldValue(
                                  field.name as keyof RegistrationRequest,
                                  value,
                                );
                              }
                            }}
                          />
                        </Form.Item>
                      </Col>
                    );
                  })}
                </Row>
              </div>

              <Button
                type="primary"
                style={{ marginTop: 16 }}
                size="large"
                htmlType="submit"
                block
                disabled={loading || !canSubmit}
                loading={loading}
              >
                Hantar
              </Button>
            </Form>
          </Card>

          <div className="mt-4">
            <Text
              style={{
                display: "block",
                color: "#D7F4FF",
                textAlign: "center",
                alignSelf: "stretch",
                fontWeight: 500,
                textShadow: "0 0 4px rgba(0, 21, 43, 0.90)",
              }}
            >
              Sebarang pertanyaan berkenaan pendaftaran, boleh hubungi pejabat
              Lembaga Pembangunan Tioman (TDA) di talian 019-9283179
            </Text>
          </div>
        </div>
      </div>
    </>
  );
}
